import React, { useState } from 'react';
import { VStack, HStack, Text, Input, Button } from '@chakra-ui/react';
import Drawer, { DrawerHeader, DrawerTitle, DrawerBody, DrawerFooter, DrawerCloseTrigger } from '@/components/ui/Drawer';
import withHashDrawer from '@/components/ui/withHashDrawer';
import { useChangeUserEmailMutation, useGetUserByIdQuery } from '../api';

interface ChangeEmailModalProps {
  isOpen: boolean;
  onClose: () => void;
  entityId?: number;
  onSuccess?: () => void;
}

const ChangeEmailModal: React.FC<ChangeEmailModalProps> = ({
  isOpen,
  onClose,
  entityId,
  onSuccess,
}) => {
  const [newEmail, setNewEmail] = useState('');
  const [error, setError] = useState('');
  const { data: user } = useGetUserByIdQuery(entityId!, {
    skip: !entityId,
  });
  
  const [changeEmail, { isLoading }] = useChangeUserEmailMutation();

  const handleSubmit = async () => {
    if (!user) return;
    
    setError('');
    
    if (!newEmail.trim()) {
      setError('Email is required');
      return;
    }
    
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newEmail)) {
      setError('Please enter a valid email address');
      return;
    }

    try {
      await changeEmail({
        id: user.id,
        email: newEmail.trim(),
      }).unwrap();
      
      setNewEmail('');
      onSuccess?.();
      onClose();
    } catch (err: any) {
      setError(err.data?.message || 'Failed to change email');
    }
  };

  const handleClose = () => {
    setNewEmail('');
    setError('');
    onClose();
  };

  return (
    <Drawer isOpen={isOpen} onClose={handleClose} size="md">
      <DrawerHeader>
        <DrawerTitle>Change Email</DrawerTitle>
        <DrawerCloseTrigger />
      </DrawerHeader>
      
      <DrawerBody>
        <VStack gap={4} align="stretch">
          {user && (
            <Text fontSize="sm" color="gray.600">
              Changing email for: <strong>{user.first_name} {user.last_name}</strong>
            </Text>
          )}
          
          <div>
            <Text fontSize="sm" fontWeight="medium" color="gray.600" mb={1}>
              Current Email
            </Text>
            <Text>{user?.email}</Text>
          </div>
          
          <div>
            <Text fontSize="sm" fontWeight="medium" color="gray.600" mb={1}>
              New Email <Text as="span" color="red.500">*</Text>
            </Text>
            <Input
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              placeholder="Enter new email address"
              type="email"
            />
            {error && (
              <Text color="red.500" fontSize="sm" mt={1}>
                {error}
              </Text>
            )}
          </div>
        </VStack>
      </DrawerBody>
      
      <DrawerFooter>
        <HStack gap={3}>
          <Button variant="outline" onClick={handleClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            loading={isLoading}
            disabled={isLoading}
            colorPalette="blue"
          >
            Change Email
          </Button>
        </HStack>
      </DrawerFooter>
    </Drawer>
  );
};

export default withHashDrawer(ChangeEmailModal, {
  action: 'change-email',
});
